import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './BugReportButton.css';

const BugReportButton = () => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    whatHappened: '',
    whatExpected: '',
    page: window.location.pathname
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleOpen = () => {
    setIsModalOpen(true);
    setSubmitStatus(null);
    // Auto-fill current page
    setFormData(prev => ({
      ...prev,
      page: window.location.pathname
    }));
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setFormData({
      whatHappened: '',
      whatExpected: '',
      page: window.location.pathname
    });
    setSubmitStatus(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/api/bug-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...formData,
          userEmail: user?.email,
          userName: user?.name,
          userId: user?.id,
          userRole: user?.role
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit bug report');
      }

      setSubmitStatus('success');

      // Close modal after 2 seconds on success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting bug report:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.whatHappened.trim() && formData.whatExpected.trim();

  return (
    <>
      {/* Floating Action Button */}
      <button
        className="bug-report-fab"
        onClick={handleOpen}
        title="Report a bug or issue"
        aria-label="Report a bug or issue"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {/* Antennae */}
          <path d="M8 1.88L9.88 3.76" />
          <path d="M16 1.88L14.12 3.76" />
          {/* Head */}
          <circle cx="12" cy="5" r="2" />
          {/* Body segments */}
          <ellipse cx="12" cy="11" rx="5" ry="4" />
          <ellipse cx="12" cy="17" rx="4" ry="3" />
          {/* Left legs */}
          <path d="M7 9L4 7" />
          <path d="M7 13L4 14" />
          <path d="M8 17L5 19" />
          {/* Right legs */}
          <path d="M17 9L20 7" />
          <path d="M17 13L20 14" />
          <path d="M16 17L19 19" />
        </svg>
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="bug-report-modal-overlay" onClick={handleClose}>
          <div className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bug-report-modal-header">
              <h2>Report an Issue</h2>
              <button
                className="bug-report-modal-close"
                onClick={handleClose}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="bug-report-form">
              <div className="form-group">
                <label htmlFor="whatHappened">
                  What happened? <span className="required">*</span>
                </label>
                <textarea
                  id="whatHappened"
                  name="whatHappened"
                  value={formData.whatHappened}
                  onChange={handleChange}
                  placeholder="Describe what you experienced..."
                  rows="4"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="whatExpected">
                  What did you expect to happen? <span className="required">*</span>
                </label>
                <textarea
                  id="whatExpected"
                  name="whatExpected"
                  value={formData.whatExpected}
                  onChange={handleChange}
                  placeholder="Describe what you expected..."
                  rows="4"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="page">Page/Location</label>
                <input
                  type="text"
                  id="page"
                  name="page"
                  value={formData.page}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              {submitStatus === 'success' && (
                <div className="bug-report-success">
                  Thank you! Your report has been submitted.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bug-report-error">
                  Failed to submit report. Please try again.
                </div>
              )}

              <div className="bug-report-modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!isFormValid || isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BugReportButton;
